// EditModel.jsx
// Used to edit a model. Functions like deleting, adding, renaming, updating all go here.
// Each function edits the model entirely, this allows for instant runs with no errors.
// This may be changed in the future when a no-exception instant model run procedure is developed.

// Delete a subcatchment
const deleteSubcatchment = (model, setModel, section, name) => {
  function deleteSectionID(model, secName, name){
    var sec = {}
    if(model && model[secName] && model[secName][name]){
      sec = {
        ...model[secName]
      }
      delete sec[name]
    }
    else {
      sec = {...model[secName]}
    }

    return sec
  }

  const SUBCATCHMENTS = deleteSectionID(model,             'SUBCATCHMENTS', name)
  const INFILTRATION  = deleteSectionID(model,             'INFILTRATION',  name)
  const GROUNDWATER   = deleteSectionID(model,             'GROUNDWATER',   name)
  const LID_USAGE     = deleteSectionID(model,             'LID_USAGE',     name)
  const COVERAGES     = deleteSectionID(model,             'COVERAGES',     name)
  const SUBAREAS      = deleteSectionID(model,             'SUBAREAS',      name)
  const POLYGONS      = deleteSectionID(model,             'POLYGONS',      name)
  const LOADINGS      = deleteSectionID(model,             'LOADINGS',      name)
  const PATTERN       = deleteSectionID(model.ADJUSTMENTS, 'PATTERN',       name)
  const GWF           = deleteSectionID(model,             'GWF',           name)

  setModel(prev=>({
    ...prev, 
    SUBAREAS     : SUBAREAS,     
    POLYGONS     : POLYGONS,     
    GROUNDWATER  : GROUNDWATER,  
    INFILTRATION : INFILTRATION, 
    GWF          : GWF,          
    LID_USAGE    : LID_USAGE,    
    LOADINGS     : LOADINGS,     
    COVERAGES    : COVERAGES,    
    ADJUSTMENTS  : {...model.ADJUSTMENTS, PATTERN: PATTERN},    
    SUBCATCHMENTS: SUBCATCHMENTS
  }))
}

// Create a junction
const createJunction = (model, setModel, newNodeName, coordinates) => {
  // Create the model structures for:
  // [COORDINATES]
  let coordinatesJSON = { 
    [newNodeName]:{
      x:coordinates.x,
      y:coordinates.y     
    }     
  }

  // [JUNCTIONS]
  let junctionsJSON = {
    [newNodeName]: {
      Invert: 0,
      Dmax: 7,
      Dinit: 0,
      Dsurch: 0,
      Aponded: 5,
      Description: ""
    }
  }

  setModel(prev=>({
    ...prev, 
    COORDINATES: {
      ...prev.COORDINATES, ...coordinatesJSON
    },
    JUNCTIONS: {
      ...prev.JUNCTIONS, ...junctionsJSON
    }
  }))
}

// Delete a link
const deleteLink = (model, setModel, section, name) => {
  if(section === 'CONDUITS'){
    const newConduits = {...model.CONDUITS}
    delete newConduits[name]

    const newXSections = {...model.XSECTIONS}
    delete newXSections[name]

    const newLosses = {...model.LOSSES}
    delete newLosses[name]

    const newVertices = {...model.VERTICES}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: newConduits,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      VERTICES: newVertices
    }))
  } else if(section === 'PUMPS'){
    const newPumps = {...model.PUMPS}
    delete newPumps[name]

    const newVertices = {...model.VERTICES}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      PUMPS: newPumps,
      VERTICES: newVertices
    }))
  } else if(section === 'ORIFICES'){
    const newOrifices = {...model.ORIFICES}
    delete newOrifices[name]

    const newXSections = {...model.XSECTIONS}
    delete newXSections[name]

    const newVertices = {...model.VERTICES}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      ORIFICES: newOrifices,
      XSECTIONS: newXSections,
      VERTICES: newVertices
    }))
  } else if(section === 'WEIRS'){
    const newWeirs = {...model.WEIRS}
    delete newWeirs[name]

    const newXSections = {...model.XSECTIONS}
    delete newXSections[name]

    const newVertices = {...model.VERTICES}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      WEIRS: newWeirs,
      XSECTIONS: newXSections,
      VERTICES: newVertices
    }))
  } else if(section === 'OUTLETS'){
    const newOutlets = {...model.OUTLETS}
    delete newOutlets[name]

    const newVertices = {...model.VERTICES}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      OUTLETS: newOutlets,
      VERTICES: newVertices
    }))
  }
}

const deleteNode = (model, setModel, section, name) => {
  // Copy all of the relevant sections
  const CONDUITS = {...model.CONDUITS}
  const PUMPS = {...model.PUMPS}
  const ORIFICES = {...model.ORIFICES}
  const WEIRS = {...model.WEIRS}
  const OUTLETS = {...model.OUTLETS}
  const newXSections = {...model.XSECTIONS}
  const newLosses = {...model.LOSSES}
  const newVertices = {...model.VERTICES}

  // Delete all copied objects that use the node
  let array1 = [CONDUITS, PUMPS, ORIFICES, WEIRS, OUTLETS]
  array1.forEach((linkSection) => {
    if(Object.keys(linkSection).length > 0){
      Object.keys(linkSection).forEach((link)=>{
        if(linkSection[link].Node1 === name || linkSection[link].Node2 === name){
          // Delete the link
          delete linkSection[link]
          delete newXSections[link]
          delete newLosses[link]
          delete newVertices[link]
        }
      })
    }
  })

  if(section === 'JUNCTIONS'){
    const newCoordinates = {...model.COORDINATES}
    delete newCoordinates[name]

    const newJunctions = {...model.JUNCTIONS}
    delete newJunctions[name]

    const newInflows = {...model.INFLOWS}
    delete newInflows[name]

    const newDWF = {...model.DWF}
    delete newDWF[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: CONDUITS,
      PUMPS:    PUMPS,
      ORIFICES: ORIFICES,
      WEIRS:    WEIRS,
      OUTLETS:  OUTLETS,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      INFLOWS: newInflows,
      DWF: newDWF,
      COORDINATES: newCoordinates,
      VERTICES: newVertices,
      JUNCTIONS: newJunctions
    }))
  }

  if(section === 'RAINGAGES'){
    const SYMBOLS = {...model.SYMBOLS}
    delete SYMBOLS[name]

    const RAINGAGES = {...model.RAINGAGES}
    delete RAINGAGES[name]

    setModel(prev=>({
      ...prev, 
      RAINGAGES: RAINGAGES,
      SYMBOLS: SYMBOLS,
    }))
  }
  
  if(section === 'OUTFALLS'){
    const newCoordinates = {...model.COORDINATES}
    delete newCoordinates[name]

    const OUTFALLS = {...model.OUTFALLS}
    delete OUTFALLS[name]

    const newInflows = {...model.INFLOWS}
    delete newInflows[name]
    
    const newDWF = {...model.DWF}
    delete newDWF[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: CONDUITS,
      PUMPS:    PUMPS,
      ORIFICES: ORIFICES,
      WEIRS:    WEIRS,
      OUTLETS:  OUTLETS,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      INFLOWS: newInflows,
      DWF: newDWF,
      COORDINATES: newCoordinates,
      VERTICES: newVertices,
      OUTFALLS: OUTFALLS
    }))
  }
  
  if(section === 'DIVIDERS'){
    const newCoordinates = {...model.COORDINATES}
    delete newCoordinates[name]

    const DIVIDERS = {...model.DIVIDERS}
    delete DIVIDERS[name]

    const newInflows = {...model.INFLOWS}
    delete newInflows[name]
    
    const newDWF = {...model.DWF}
    delete newDWF[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: CONDUITS,
      PUMPS:    PUMPS,
      ORIFICES: ORIFICES,
      WEIRS:    WEIRS,
      OUTLETS:  OUTLETS,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      INFLOWS: newInflows,
      DWF: newDWF,
      COORDINATES: newCoordinates,
      VERTICES: newVertices,
      DIVIDERS: DIVIDERS
    }))
  }
  
  if(section === 'STORAGE'){
    const newCoordinates = {...model.COORDINATES}
    delete newCoordinates[name]

    const STORAGE = {...model.STORAGE}
    delete STORAGE[name]

    const newInflows = {...model.INFLOWS}
    delete newInflows[name]
    
    const newDWF = {...model.DWF}
    delete newDWF[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: CONDUITS,
      PUMPS:    PUMPS,
      ORIFICES: ORIFICES,
      WEIRS:    WEIRS,
      OUTLETS:  OUTLETS,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      INFLOWS: newInflows,
      DWF: newDWF,
      COORDINATES: newCoordinates,
      VERTICES: newVertices,
      STORAGE: STORAGE
    }))
  }
}

const renameNode = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  // Copy all of the relevant sections
  const CONDUITS = {...model.CONDUITS}
  const PUMPS = {...model.PUMPS}
  const ORIFICES = {...model.ORIFICES}
  const WEIRS = {...model.WEIRS}
  const OUTLETS = {...model.OUTLETS}

  const INFLOWS = {
    ...model.INFLOWS, 
    [newName]: {...model.INFLOWS[name]}
  }
  delete INFLOWS[name]
  
  const DWF = {
    ...model.DWF, 
    [newName]: {...model.DWF[name]}
  }
  delete DWF[name]
  
  const COORDINATES = {
    ...model.COORDINATES, 
    [newName]: {...model.COORDINATES[name]}
  }
  delete COORDINATES[name]

  const SECTION = {
    ...model[section],
    [newName]: {...model[section][name]}
  }
  delete SECTION[name]

  // Rename nodes for all copied objects that use the node
  let array1 = [CONDUITS, PUMPS, ORIFICES, WEIRS, OUTLETS]
  array1.forEach((linkSection) => {
    if(Object.keys(linkSection).length > 0){
      Object.keys(linkSection).forEach((link)=>{
        if(linkSection[link].Node1 === name){
          // Update the link
          linkSection[link].Node1 = newName
        }
        if(linkSection[link].Node2 === name){
          // Update the link
          linkSection[link].Node2 = newName
        }
      })
    }
  })

  setModel(prev=>({
    ...prev, 
    CONDUITS: CONDUITS,
    PUMPS:    PUMPS,
    ORIFICES: ORIFICES,
    WEIRS:    WEIRS,
    OUTLETS:  OUTLETS,
    INFLOWS:  INFLOWS,
    DWF:      DWF,
    COORDINATES: COORDINATES,
    [section]: SECTION
  }))
}
const renamePollutant = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  // Copy all of the relevant sections
  let BUILDUP = {
    ...model.BUILDUP
  }
  let WASHOFF = {
    ...model.WASHOFF
  }
  let LOADINGS = {
    ...model.LOADINGS
  }
  
  // Rename pollutant for all copied objects that use the pollutant
  if(Object.keys(model.BUILDUP).length > 0){
    Object.keys(model.BUILDUP).forEach((lu)=>{
      if(model.BUILDUP[lu][name] != null){
        // Update the lu
        BUILDUP = {
          ...BUILDUP, 
          [lu]: {
            ...BUILDUP[lu],
            [newName]: {...BUILDUP[lu][name]}
          }
        }
        delete BUILDUP[lu][name]
      }
    })
  }
  
  if(Object.keys(model.WASHOFF).length > 0){
    Object.keys(model.WASHOFF).forEach((lu)=>{
      if(model.WASHOFF[lu][name] != null){
        // Update the lu
        WASHOFF = {
          ...WASHOFF, 
          [lu]: {
            ...WASHOFF[lu],
            [newName]: {...WASHOFF[lu][name]}
          }
        }
        delete WASHOFF[lu][name]
      }
    })
  }
  
  if(Object.keys(model.LOADINGS).length > 0){
    Object.keys(model.LOADINGS).forEach((lu)=>{
      if(model.LOADINGS[lu][name] != null){
        // Update the lu
        LOADINGS = {
          ...LOADINGS, 
          [lu]: {
            ...LOADINGS[lu],
            [newName]: {...LOADINGS[lu][name]}
          }
        }
        delete LOADINGS[lu][name]
      }
    })
  }

  const SECTION = {
    ...model[section],
    [newName]: {...model[section][name]}
  }
  delete SECTION[name]

  setModel(prev=>({
    ...prev, 
    BUILDUP: BUILDUP,
    WASHOFF: WASHOFF,
    LOADINGS: LOADINGS,
    [section]: SECTION
  }))
}

const renameLandUse = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  // Copy all of the relevant sections
  //const BUILDUP = {...model.BUILDUP}
  //const WASHOFF = {...model.WASHOFF}

  const BUILDUP = {
    ...model.BUILDUP, 
    [newName]: {...model.BUILDUP[name]}
  }
  delete BUILDUP[name]
  
  const WASHOFF = {
    ...model.WASHOFF, 
    [newName]: {...model.WASHOFF[name]}
  }
  delete WASHOFF[name]

  let COVERAGES = {
    ...model.COVERAGES
  }
  
  // Rename landuses for all copied objects that use the landuse
  if(Object.keys(model.COVERAGES).length > 0){
    Object.keys(model.COVERAGES).forEach((sc)=>{
      if(model.COVERAGES[sc][name] != null){
        // Update the sc
        COVERAGES = {
          ...COVERAGES, 
          [sc]: {
            ...COVERAGES[sc],
            [newName]: {...COVERAGES[sc][name]}
          }
        }
        delete COVERAGES[sc][name]
      }
    })
  }

  const SECTION = {
    ...model[section],
    [newName]: {...model[section][name]}
  }
  delete SECTION[name]

  setModel(prev=>({
    ...prev, 
    BUILDUP: BUILDUP,
    WASHOFF:   WASHOFF,
    COVERAGES: COVERAGES,
    [section]: SECTION
  }))
}

const renameLink = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  if(section === 'CONDUITS'){
    const SECTION = {
      ...model[section],
      [newName]: {...model[section][name]}
    }
    delete SECTION[name]

    const newXSections = {...model.XSECTIONS,
      [newName]: {...model.XSECTIONS[name]}}
    delete newXSections[name]

    const newLosses = {...model.LOSSES,
      [newName]: {...model.LOSSES[name]}}
    delete newLosses[name]

    const newVertices = {...model.VERTICES,
      [newName]: {...model.VERTICES[name]}}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      CONDUITS: SECTION,
      XSECTIONS: newXSections,
      LOSSES: newLosses,
      VERTICES: newVertices
    }))
  } else if(section === 'PUMPS'){
    const SECTION = {
      ...model[section],
      [newName]: {...model[section][name]}
    }
    delete SECTION[name]

    const newVertices = {...model.VERTICES,
      [newName]: {...model.VERTICES[name]}}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      PUMPS: SECTION,
      VERTICES: newVertices
    }))
  } else if(section === 'ORIFICES'){
    const SECTION = {
      ...model[section],
      [newName]: {...model[section][name]}
    }
    delete SECTION[name]

    const newXSections = {...model.XSECTIONS,
      [newName]: {...model.XSECTIONS[name]}}
    delete newXSections[name]

    const newVertices = {...model.VERTICES,
      [newName]: {...model.VERTICES[name]}}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      ORIFICES: SECTION,
      XSECTIONS: newXSections,
      VERTICES: newVertices
    }))
  } else if(section === 'WEIRS'){
    const SECTION = {
      ...model[section],
      [newName]: {...model[section][name]}
    }
    delete SECTION[name]

    const newXSections = {...model.XSECTIONS,
      [newName]: {...model.XSECTIONS[name]}}
    delete newXSections[name]

    const newVertices = {...model.VERTICES,
      [newName]: {...model.VERTICES[name]}}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      WEIRS: SECTION,
      XSECTIONS: newXSections,
      VERTICES: newVertices
    }))
  } else if(section === 'OUTLETS'){
    const SECTION = {
      ...model[section],
      [newName]: {...model[section][name]}
    }
    delete SECTION[name]

    const newVertices = {...model.VERTICES,
      [newName]: {...model.VERTICES[name]}}
    delete newVertices[name]

    setModel(prev=>({
      ...prev, 
      OUTLETS: SECTION,
      VERTICES: newVertices
    }))
  }
}

const subdivideLink = (model, setModel, section, name, divisionCount) => {
  if (section !== 'CONDUITS'){
    return;
  }

  // Get a list of new node names
  
  // Prepare data structures for new nodes and links
  let usNode = model[section][name].Node1
  let dsNode = model[section][name].Node2
  let newNodeNames = []
  let newLinkNames = []
  let usedNodeNames = [
    ...Object.keys(model.SUBCATCHMENTS),  
    ...Object.keys(model.COORDINATES),
  ]
  let usedLinkNames = [
    ...Object.keys(model.CONDUITS), 
    ...Object.keys(model.PUMPS), 
    ...Object.keys(model.ORIFICES), 
    ...Object.keys(model.WEIRS), 
    ...Object.keys(model.OUTLETS), 
  ]
  // push the first node onto newNodeNames
  newNodeNames.push(usNode)

  // Get new node and link names
  for (let j = 0; j < divisionCount; j++){
    let newNodeName = ''
    let newLinkName = ''
    for(let i=1; i<100000 && newNodeName === '' && j < divisionCount - 1; i++){
      if(!usedNodeNames.includes(i.toString())){
        newNodeName = i.toString()
        newNodeNames.push(newNodeName)
        usedNodeNames.push(newNodeName)
      }
    }
    for(let i=1; i<100000 && newLinkName === ''; i++){
      if(!usedLinkNames.includes(i.toString())){
        newLinkName = i.toString()
        newLinkNames.push(newLinkName)
        usedLinkNames.push(newLinkName)
      }
    }
  }

  // Push the last node onto newNodeNames
  newNodeNames.push(dsNode)
  
  // Create new conduits for every division.
  const SECTION = {...model[section]}
  const XSECTIONS = {...model.XSECTIONS}
  const LOSSES = {...model.LOSSES}
  const VERTICES = {...model.VERTICES}
  const COORDINATES = {...model.COORDINATES}
  const JUNCTIONS = {...model.JUNCTIONS}
  
  // Create new nodes for the divisions.
  let length_x = model.COORDINATES[dsNode].x-model.COORDINATES[usNode].x
  let length_y = model.COORDINATES[dsNode].y-model.COORDINATES[usNode].y
  let usNodeType = ''
  let dsNodeType = ''

  if(Object.keys(model.JUNCTIONS).includes(usNode)){
    usNodeType = 'JUNCTIONS'
  }
  if(Object.keys(model.OUTFALLS ).includes(usNode)){
    usNodeType = 'OUTFALLS'
  }
  if(Object.keys(model.DIVIDERS ).includes(usNode)){
    usNodeType = 'DIVIDERS'
  }
  if(Object.keys(model.STORAGE  ).includes(usNode)){
    usNodeType = 'STORAGE'
  }
  if(Object.keys(model.JUNCTIONS).includes(dsNode)){
    dsNodeType = 'JUNCTIONS'
  }
  if(Object.keys(model.OUTFALLS ).includes(dsNode)){
    dsNodeType = 'OUTFALLS'
  }
  if(Object.keys(model.DIVIDERS ).includes(dsNode)){
    dsNodeType = 'DIVIDERS'
  }
  if(Object.keys(model.STORAGE  ).includes(dsNode)){
    dsNodeType = 'STORAGE'
  }

  let invertDelta = model[usNodeType][usNode].Invert-model[dsNodeType][dsNode].Invert
  for(let i = 1; i < divisionCount; i++){
    let coords = {
      x: model.COORDINATES[usNode].x + length_x * i/divisionCount,
      y: model.COORDINATES[usNode].y + length_y * i/divisionCount
    }

    COORDINATES[newNodeNames[i]] = coords
    JUNCTIONS[newNodeNames[i]] = {
      Invert: model[usNodeType][usNode].Invert - invertDelta * i/divisionCount,
      Dmax: model[usNodeType][usNode].Dmax,
      Dinit: 0,
      Dsurch: model[usNodeType][usNode].Dsurch,
      Aponded: model[usNodeType][usNode].Aponded,
      Description: ""
    }
  }

  for(let i = 0; i < divisionCount; i++){
    SECTION[newLinkNames[i]] = {...model[section][name]}
    XSECTIONS[newLinkNames[i]] = {...model.XSECTIONS[name]}
    LOSSES[newLinkNames[i]] = {...model.LOSSES[name]}

    // Adjust sizes and us/ds nodes
    SECTION[newLinkNames[i]].Length = model[section][name].Length/divisionCount
    SECTION[newLinkNames[i]].InOffset = 0
    SECTION[newLinkNames[i]].OutOffset = 0
    SECTION[newLinkNames[i]].Node1 = newNodeNames[i]
    SECTION[newLinkNames[i]].Node2 = newNodeNames[i+1]
  }

  // delete the source conduit
  delete SECTION[name]
  delete XSECTIONS[name]
  delete LOSSES[name]
  delete VERTICES[name]

  setModel(prev=>({
    ...prev, 
    CONDUITS: SECTION,
    XSECTIONS: XSECTIONS,
    LOSSES: LOSSES,
    VERTICES: VERTICES,
    COORDINATES: COORDINATES,
    JUNCTIONS: JUNCTIONS
  }))
}

const renameRaingage = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  // Copy all of the relevant sections
  const SECTION = {
    ...model[section],
    [newName]: {...model[section][name]}
  }
  delete SECTION[name]

  function updateSection(model, secName, name, newName){
    var sec = {}
    if(model && model[secName] && model[secName][name]){
      sec = {
        ...model[secName], 
        [newName]: model[secName][name]
      }
      delete sec[name]
    }
    else {
      sec = {...model[secName]}
    }

    return sec
  }

  const SYMBOLS     = updateSection(model, 'SYMBOLS',     name, newName)

  setModel(prev=>({
    ...prev,    
    SYMBOLS     : SYMBOLS,        
    [section]: SECTION
  }))
}

const renameSubcatchment = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]
  // Copy all of the relevant sections
  const SECTION = {
    ...model[section],
    [newName]: {...model[section][name]}
  }
  delete SECTION[name]

  function updateSection(model, secName, name, newName){
    var sec = {}
    if(model && model[secName] && model[secName][name]){
      sec = {
        ...model[secName], 
        [newName]: model[secName][name]
      }
      delete sec[name]
    }
    else {
      sec = {...model[secName]}
    }

    return sec
  }

  const INFILTRATION = updateSection(model,             'INFILTRATION', name, newName)
  const GROUNDWATER  = updateSection(model,             'GROUNDWATER',  name, newName)
  const LID_USAGE    = updateSection(model,             'LID_USAGE',    name, newName)
  const COVERAGES    = updateSection(model,             'COVERAGES',    name, newName)
  const SUBAREAS     = updateSection(model,             'SUBAREAS',     name, newName)
  const POLYGONS     = updateSection(model,             'POLYGONS',     name, newName)
  const LOADINGS     = updateSection(model,             'LOADINGS',     name, newName)
  const PATTERN      = updateSection(model.ADJUSTMENTS, 'PATTERN',      name, newName)
  const GWF          = updateSection(model,             'GWF',          name, newName)

  setModel(prev=>({
    ...prev, 
    SUBAREAS     : SUBAREAS,     
    POLYGONS     : POLYGONS,     
    GROUNDWATER  : GROUNDWATER,  
    INFILTRATION : INFILTRATION, 
    GWF          : GWF,          
    LID_USAGE    : LID_USAGE,    
    LOADINGS     : LOADINGS,     
    COVERAGES    : COVERAGES,    
    ADJUSTMENTS  : {...model.ADJUSTMENTS, PATTERN: PATTERN},    
    [section]: SECTION
  }))
}

const renameCurve = (model, setModel, section, subsection, name, newName) => {
  newName = newName.split(' ')[0]

  const SUBSECTION = {
    ...model[section][subsection],
    [newName]: model[section][subsection][name]
  }

  delete SUBSECTION[name]

  setModel(prev=>({
    ...prev, 
    CURVES: {
      ...model.CURVES,
      [subsection]: SUBSECTION
    }
  }))
}

const renameObject = (model, setModel, section, name, newName) => {
  newName = newName.split(' ')[0]

  const SECTION = {
    ...model[section],
    [newName]: model[section][name]
  }

  delete SECTION[name]

  setModel(prev=>({
    ...prev, 
    [section]: SECTION
  }))
}

export { deleteLink, deleteNode, deleteSubcatchment, renameNode, renameLink, subdivideLink, renameSubcatchment, renameRaingage, renameCurve, renameObject, renameLandUse, renamePollutant, createJunction }